import { useLayoutEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { Tooltip, UnstyledButton } from '@buffer-mono/popcorn'

import { normalizeNetworkName } from '~publish/helpers/channels'

import { ChannelLegendItem } from '../../components/ChannelLegendItem'
import type { ChannelLegendData } from '../../components/ChannelLegendItem'

import { countFittingItems } from './countFittingItems'
import styles from './ChannelLegend.module.css'

interface ChannelLegendProps {
  /** Visible channels, one entry per channel. */
  channels: ChannelLegendData[]
}

// Small horizontal reserve so entries never crowd the legend's edge; once the
// natural width exceeds the space beyond it, entries collapse into a "+N more"
// affordance.
const MIN_LEFT_GAP_PX = 24
// Width reserved for the "+N more" label when deciding how many channel entries
// fit. A small over-estimate simply shows one fewer entry — never a clipped one.
const MORE_LABEL_RESERVE_PX = 72

/**
 * Renders an entry (colour swatch + channel icon + name) per visible channel
 * and, when they don't all fit, collapses the overflow into a "+N more" label
 * whose tooltip lists the rest.
 *
 * How many entries fit is measured from an off-screen "ghost" row holding the
 * full set, so the calculation is independent of how many are currently shown.
 */
export function ChannelLegend({
  channels,
}: ChannelLegendProps): JSX.Element | null {
  const containerRef = useRef<HTMLDivElement>(null)
  const ghostRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(channels.length)

  useLayoutEffect(() => {
    const container = containerRef.current
    const ghost = ghostRef.current
    if (!container || !ghost) return undefined

    const measure = (): void => {
      // offsetLeft already accounts for the inter-entry gaps in the ghost row.
      const rightEdges: number[] = []
      for (const child of Array.from(ghost.children)) {
        if (child instanceof HTMLElement) {
          rightEdges.push(child.offsetLeft + child.offsetWidth)
        }
      }
      setVisibleCount(
        countFittingItems(
          rightEdges,
          container.clientWidth - MIN_LEFT_GAP_PX,
          MORE_LABEL_RESERVE_PX,
        ),
      )
    }

    measure()
    // Observe both the container (its available width) and the ghost row (its
    // natural width grows when web fonts or channel icons finish loading after
    // the first paint). Without watching the ghost, the initial measure can run
    // against the fallback font and never re-run, leaving the legend overflowing
    // instead of collapsing into "+N more".
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    observer.observe(ghost)
    return (): void => observer.disconnect()
  }, [channels])

  if (channels.length === 0) return null

  const visible = channels.slice(0, visibleCount)
  const hidden = channels.slice(visibleCount)

  return (
    <div
      ref={containerRef}
      className={styles.legend}
      // A named group describes the visible channel series without adding a page landmark.
      // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="group"
      aria-label="Visible channels legend"
    >
      {/* Off-screen full set, used only to measure how many entries fit. */}
      <div ref={ghostRef} className={styles.ghost} aria-hidden="true">
        {channels.map((channel) => (
          <ChannelLegendItem key={channel.id} channel={channel} />
        ))}
      </div>
      {visible.map((channel) => (
        <ChannelLegendItem key={channel.id} channel={channel} />
      ))}
      {hidden.length > 0 && (
        <Tooltip
          content={
            <div className={styles.moreList}>
              {hidden.map((channel) => (
                // `inverted` icons so the neutral (dark) glyphs stay visible on
                // the tooltip's dark background.
                <ChannelLegendItem
                  key={channel.id}
                  channel={channel}
                  iconColor="inverted"
                />
              ))}
            </div>
          }
        >
          {/* Popcorn UnstyledButton lets keyboard / screen-reader users reach this
              control with the focus-visible ring while it reads as plain text — no
              button chrome or hover affordance. The aria-label names the hidden
              channels directly, so VoiceOver announces them without relying on
              the visual tooltip mounting. */}
          <UnstyledButton
            type="button"
            className={styles.moreLabel}
            aria-label={`${hidden.length} more ${
              hidden.length === 1 ? 'channel' : 'channels'
            }: ${hidden
              .map(
                (channel) =>
                  `${normalizeNetworkName(channel.service)} ${channel.name}`,
              )
              .join(', ')}`}
          >
            +{hidden.length} more
          </UnstyledButton>
        </Tooltip>
      )}
    </div>
  )
}
